// Reusable hook: upload a file with progress to one of our API routes via XHR
// (fetch can't report upload progress).
import { useCallback, useRef, useState } from 'react';
import { getApiBase } from '@/lib/http';
import { getAuthToken } from '@/auth/token';
export function useUpload() {
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState(null);
    const xhrRef = useRef(null);
    const cancel = useCallback(() => {
        xhrRef.current?.abort();
    }, []);
    const upload = useCallback((path, fields, file) => new Promise((resolve, reject) => {
        setUploading(true);
        setProgress(0);
        setError(null);
        const xhr = new XMLHttpRequest();
        xhrRef.current = xhr;
        xhr.open('POST', `${getApiBase()}/api${path}`);
        xhr.upload.onprogress = (e) => {
            if (e.lengthComputable)
                setProgress(e.loaded / e.total);
        };
        xhr.onload = () => {
            setUploading(false);
            if (xhr.status >= 200 && xhr.status < 300) {
                try {
                    resolve(JSON.parse(xhr.responseText));
                }
                catch {
                    resolve(xhr.responseText);
                }
            }
            else {
                const msg = `Upload failed (${xhr.status})`;
                setError(msg);
                reject(new Error(msg));
            }
        };
        xhr.onerror = () => {
            setUploading(false);
            const msg = 'Network error during upload';
            setError(msg);
            reject(new Error(msg));
        };
        xhr.onabort = () => {
            setUploading(false);
            const msg = 'Upload cancelled';
            setError(msg);
            reject(new Error(msg));
        };
        const form = new FormData();
        for (const [k, v] of Object.entries(fields))
            form.append(k, v);
        form.append('file', file);
        getAuthToken()
            .then((token) => {
            if (token)
                xhr.setRequestHeader('Authorization', `Bearer ${token}`);
            xhr.send(form);
        })
            .catch((err) => {
            setUploading(false);
            const msg = err.message || 'Unable to acquire authentication token';
            setError(msg);
            reject(new Error(msg));
        });
    }), []);
    return { uploading, progress, error, cancel, upload };
}
